import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input, FormFeedback, Button } from "reactstrap";

// The backend field names (StoreProductQuestionAnswerRequest / UpdateProductQuestionAnswerRequest)
// that this form has a matching input for — anything outside this set (e.g. product_id)
// has nowhere to render inline and falls back to the general error banner instead.
const KNOWN_FIELDS = ["question", "answer"];

// 422 responses reject with the raw Laravel {message, errors: {field: [...]}} payload
// itself (not an AxiosError — see the shared axios interceptor, src/utils/api.jsx, and
// the same convention already relied on by wearItToFormModal.jsx). Splits that payload
// into { fieldErrors: {field: message}, generalMessage } so known fields render inline
// against their own input and everything else still surfaces somewhere.
const splitApiError = (err, fallback) => {
  const isStructured = err && typeof err === "object" && !err.isAxiosError && err.errors && typeof err.errors === "object";

  if (!isStructured) {
    return { fieldErrors: {}, generalMessage: err?.response?.data?.message || err?.message || fallback };
  }

  const fieldErrors = {};
  let generalMessage = "";

  Object.entries(err.errors).forEach(([field, messages]) => {
    const first = Array.isArray(messages) && messages.length ? messages[0] : null;
    if (!first) return;
    if (KNOWN_FIELDS.includes(field)) {
      fieldErrors[field] = first;
    } else if (!generalMessage) {
      generalMessage = first;
    }
  });

  if (!generalMessage && Object.keys(fieldErrors).length === 0) {
    generalMessage = err.message || fallback;
  }

  return { fieldErrors, generalMessage };
};

const EMPTY_FORM = { question: "", answer: "" };
const EMPTY_FIELD_ERRORS = { question: "", answer: "" };

/**
 * Single reusable create/edit form for a product's Question & Answer entry — just two
 * text fields, no file handling at all. Mirrors the field wiring, required-field gating,
 * and submit/close behaviour of wearItToFormModal.jsx, minus everything file/preview
 * related (nothing to upload here).
 *
 * This component never calls the API directly — `onSubmit` is a caller-supplied async
 * function (the parent, manageQuestionsAnswersModal.jsx, wires it to the real create/update
 * endpoints) that receives the validated { question, answer } and is awaited purely to
 * drive the in-flight/disabled state and surface any rejection as field-level errors
 * against the matching input (falls back to a general banner for anything else, e.g. a
 * product_id error).
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - mode: "create" | "edit"
 *  - record: the existing row when mode === "edit" ({ question, answer })
 *  - onSubmit: ({ question, answer }) => Promise
 */
const QuestionAnswerFormModal = ({ open, onClose, mode, record, onSubmit }) => {
  const isEditing = mode === "edit";

  const [values, setValues] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState(EMPTY_FIELD_ERRORS);
  const [generalError, setGeneralError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset/pre-populate whenever the modal is (re)opened, and whenever the target record
  // changes — so switching which row is being edited never leaves stale form state.
  useEffect(() => {
    if (!open) return;

    setFieldErrors(EMPTY_FIELD_ERRORS);
    setGeneralError("");
    setSubmitting(false);

    if (isEditing && record) {
      setValues({ question: record.question || "", answer: record.answer || "" });
    } else {
      setValues(EMPTY_FORM);
    }
  }, [open, isEditing, record]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;

    const question = values.question.trim();
    const answer = values.answer.trim();
    const clientErrors = { ...EMPTY_FIELD_ERRORS };

    if (!question) {
      clientErrors.question = "Question is required.";
    }
    if (!answer) {
      clientErrors.answer = "Answer is required.";
    }

    if (clientErrors.question || clientErrors.answer) {
      setFieldErrors(clientErrors);
      setGeneralError("");
      return;
    }

    setFieldErrors(EMPTY_FIELD_ERRORS);
    setGeneralError("");
    setSubmitting(true);

    Promise.resolve(onSubmit({ question, answer }))
      .catch((err) => {
        const { fieldErrors: serverFieldErrors, generalMessage } = splitApiError(err, "Failed to save. Please try again.");
        setFieldErrors({ ...EMPTY_FIELD_ERRORS, ...serverFieldErrors });
        setGeneralError(generalMessage);
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Modal isOpen={open} toggle={onClose} className="modal-dialog-centered" size="md">
      <ModalHeader toggle={onClose} tag="div">
        {isEditing ? "Edit Question & Answer" : "Add Question & Answer"}
      </ModalHeader>
      <ModalBody>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Question</Label>
            <Input
              type="textarea"
              rows={3}
              value={values.question}
              onChange={(e) => setValues((v) => ({ ...v, question: e.target.value }))}
              invalid={Boolean(fieldErrors.question)}
            />
            {fieldErrors.question && <FormFeedback>{fieldErrors.question}</FormFeedback>}
          </FormGroup>

          <FormGroup>
            <Label>Answer</Label>
            <Input
              type="textarea"
              rows={6}
              value={values.answer}
              onChange={(e) => setValues((v) => ({ ...v, answer: e.target.value }))}
              invalid={Boolean(fieldErrors.answer)}
            />
            {fieldErrors.answer && <FormFeedback>{fieldErrors.answer}</FormFeedback>}
          </FormGroup>

          {generalError && <div className="text-danger small mb-2">{generalError}</div>}

          <div className="d-flex" style={{ gap: 8 }}>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? "Saving..." : "Save"}
            </Button>
            <Button color="secondary" type="button" outline onClick={onClose}>
              Cancel
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default QuestionAnswerFormModal;
